import type { Product } from '@shopgnd/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { ProductDraft } from '@/api/catalog';
import { cognitoAuthFailureMessage } from '@/lib/cognitoSessionHint';
import { resolveProductListingUrls } from '@/lib/productListingUrl';
import {
  fetchLambdaWithProxyFallback,
  isLambdaFetchUnreachableError,
  isLambdaNotConfiguredError,
  lambdaFetchUnavailableMessage,
  lambdaNotConfiguredMessage,
  parseLambdaErrorResponse,
} from '@/lib/lambdaFetch';

async function productListingFetch(init: RequestInit): Promise<Response> {
  const endpoints = await resolveProductListingUrls();
  if (!endpoints) throw new Error(lambdaNotConfiguredMessage('Product listing service'));

  const doFetch = async (forceRefresh: boolean) => {
    let session;
    try {
      session = await fetchAuthSession({ forceRefresh });
    } catch {
      throw new Error('Could not verify your sign-in session. Sign in again and retry.');
    }
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('Sign in required.');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers as Record<string, string> | undefined),
    };
    const res = await fetchLambdaWithProxyFallback(endpoints.proxyPath, endpoints.directUrl, {
      ...init,
      headers,
    });
    return { res, token };
  };

  try {
    let { res, token } = await doFetch(false);
    if (res.status === 401) {
      ({ res, token } = await doFetch(true));
    }
    if (!res.ok && res.status === 401) {
      const apiError = await parseLambdaErrorResponse(res);
      throw new Error(await cognitoAuthFailureMessage(401, apiError, token));
    }
    return res;
  } catch (e) {
    if (e instanceof Error && e.message === 'Sign in required.') throw e;
    if (e instanceof Error && e.message.startsWith('Could not verify your sign-in')) throw e;
    if (isLambdaNotConfiguredError(e)) throw e;
    if (isLambdaFetchUnreachableError(e)) {
      throw new Error(lambdaFetchUnavailableMessage('Product listing service'));
    }
    throw e;
  }
}

function productListingBody(vendorId: string, draft: Partial<ProductDraft>, action: 'create' | 'update', productId?: string) {
  return JSON.stringify({
    action,
    productId,
    vendorId,
    title: draft.title,
    slug: draft.slug,
    description: draft.description,
    shortDescription: draft.shortDescription,
    priceCents: draft.priceCents,
    currency: draft.currency,
    categoryId: draft.categoryId,
    categoryIds: draft.categoryIds,
    images: draft.images,
    stock: draft.stock,
    parish: draft.parish,
    listingStatus: draft.listingStatus,
    condition: draft.condition,
    compareAtCents: draft.compareAtCents,
    variants: draft.variants,
    variantMultiSelect: draft.variantMultiSelect,
    variantConfig: draft.variantConfig,
    pairsWith: draft.pairsWith,
    isLocallyMade: draft.isLocallyMade,
    seoTitle: draft.seoTitle,
    seoDescription: draft.seoDescription,
    searchKeywords: draft.searchKeywords,
    imageAlts: draft.imageAlts ?? draft.altTexts,
    altTexts: draft.altTexts ?? draft.imageAlts,
    seoStatus: draft.seoStatus,
    lowStockThreshold: draft.lowStockThreshold,
    allowBackorders: draft.allowBackorders,
    weightKg: draft.weightKg,
    shippingClass: draft.shippingClass,
    pickupAvailable: draft.pickupAvailable,
    pickupLocation: draft.pickupLocation,
    pickupLatitude: draft.pickupLatitude,
    pickupLongitude: draft.pickupLongitude,
    localDeliveryOnly: draft.localDeliveryOnly,
    deliveryPriceCents: draft.deliveryPriceCents,
    deliveryParishRates: draft.deliveryParishRates,
    costCents: draft.costCents,
    sku: draft.sku,
    barcode: draft.barcode,
    taxable: draft.taxable,
    lengthCm: draft.lengthCm,
    widthCm: draft.widthCm,
    heightCm: draft.heightCm,
    featured: draft.featured,
  });
}

/** Network / CORS / misconfiguration — caller should fail closed (no AppSync quota bypass). */
export function isProductListingLambdaUnreachableError(error: unknown): boolean {
  return (
    isLambdaFetchUnreachableError(error) ||
    (error instanceof Error &&
      error.message.includes('Product listing service could not be reached'))
  );
}

/** Server-side product create with quota enforcement. */
export async function createProductViaLambda(
  vendorId: string,
  draft: ProductDraft,
): Promise<{ id: string; slug: string }> {
  const res = await productListingFetch({
    method: 'POST',
    body: productListingBody(vendorId, draft, 'create'),
  });

  if (!res.ok) {
    throw new Error(await parseLambdaErrorResponse(res));
  }

  const data = (await res.json()) as { product: { id: string; slug: string } };
  return data.product;
}

/** Server-side product update — org staff + owner (bypasses AppSync owner-only auth). */
export async function updateProductViaLambda(
  productId: string,
  vendorId: string,
  draft: Partial<ProductDraft>,
): Promise<{ id: string; slug: string }> {
  const res = await productListingFetch({
    method: 'POST',
    body: productListingBody(vendorId, draft, 'update', productId),
  });

  if (!res.ok) {
    throw new Error(await parseLambdaErrorResponse(res));
  }

  const data = (await res.json()) as { product: { id: string; slug: string } };
  return data.product;
}

export type { Product };
